import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useState } from 'react';

import { LogoWithFallbackProps } from './LogoWithFallback.props';

/**
 * Renders a small logo image. If the image URL is broken (fails to load), it
 * falls back to MUI's BrokenImageIcon with the first two characters of `code`
 * overlaid — a clean, length-proof stand-in for the ugly native broken-image
 * glyph. When there is no `src` at all it renders nothing (an absent logo is not
 * a broken one).
 */
export const LogoWithFallback: React.FC<LogoWithFallbackProps> = ({
  src,
  code,
  label,
  height = 22,
  maxWidth = 44,
}) => {
  const [errored, setErrored] = useState(false);

  // A new src is a fresh image — clear any prior error so a reused instance
  // (e.g. the same row position after pagination) doesn't show the broken-image
  // fallback for a different, valid logo.
  useEffect(() => setErrored(false), [src]);

  if (!src) return null;

  if (errored) {
    const initials = (code ?? '').trim().slice(0, 2).toUpperCase();
    return (
      <Tooltip title={label} placement="top" arrow>
        <Box
          role="img"
          aria-label={label}
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height,
            minWidth: height,
            flexShrink: 0,
          }}
        >
          <BrokenImageIcon sx={{ fontSize: height, color: 'text.disabled' }} />
          {initials && (
            <Box
              component="span"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: Math.round(height * 0.4),
                fontWeight: 700,
                color: 'text.secondary',
                pointerEvents: 'none',
              }}
            >
              {initials}
            </Box>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={label} placement="top" arrow>
      <Box
        component="img"
        src={src}
        alt={label}
        onError={() => setErrored(true)}
        sx={{ height, maxWidth, objectFit: 'contain', display: 'block', flexShrink: 0 }}
      />
    </Tooltip>
  );
};
